/**
 * widgets/ModulePanel.tsx — modulová stránka JAKO widget panel se ZAMČENÝM
 * DEFAULTEM (Filip 2026-07-11): default layout určuje vývojář a NEJDE přepsat
 * („uložit jako výchozí" tu není). Uživatel si ale rozvržení může odemknout a
 * upravit — jeho verze se ukládá jako per-user override do ui.prefs
 * (`module-panel:<id>`); „Obnovit výchozí" override smaže a vrátí vývojářův
 * default. Název záložky se měnit nedá.
 *
 * forceNode / onNodeChange — Node workspace: stejný panel, cílový nod z URL
 * (ne z per-user prefs). Změna NodePicker → navigace na jiný nod, ne persist.
 */
import { useEffect, useMemo, useState } from 'react';
import { mcpCall } from '../lib/mcp';
import { WidgetPanel } from './WidgetPanel';
import type { WidgetPanel as Panel } from './types';

/** Per-user override tvaru { widgets, groups, node }; {} = žádný override. */
type Override = Partial<Pick<Panel, 'widgets' | 'groups' | 'node'>>;

export interface ModulePanelProps {
  panel: Panel;
  /**
   * Pokud je definováno (včetně null = lokál), panel vždy běží na tomto nodu.
   * NodePicker volá onNodeChange místo zápisu do session state panelu.
   */
  forceNode?: string | null;
  onNodeChange?: (node: string | null) => void;
  /** Modul stránky — WidgetPicker ho nabídne jako první kategorii. */
  preferredModule?: string | null;
}

export function ModulePanel(
  { panel: def, forceNode, onNodeChange, preferredModule }: ModulePanelProps,
) {
  const [p, setP] = useState<Panel>(def);
  const [editing, setEditing] = useState(false);
  const forced = forceNode !== undefined;

  // Po mountu / změně def.id dotáhni per-user override (id/label vždy z defaultu).
  useEffect(() => {
    let alive = true;
    // Base layout z vývojáře; node z forceNode nebo def
    setP({
      ...def,
      node: forced ? forceNode : (def.node ?? null),
    });
    mcpCall<{ value?: Override | null }>('ui.prefs_get', { key: `module-panel:${def.id}` })
      .then((r) => {
        const v = r?.value;
        if (alive && v && Array.isArray(v.widgets)) {
          // Nové defaulty doplnit. mesh-fleet: starý dual topology+fleet z prefs vyhodit.
          const drop = def.id === 'mesh-fleet' ? new Set(['mesh.topology']) : null;
          const cleaned = drop
            ? v.widgets.filter((w) => !drop.has(w.widgetId))
            : v.widgets;
          const seen = new Set(cleaned.map((w) => w.widgetId));
          const mergedWidgets = [
            ...cleaned,
            ...def.widgets.filter((w) => !seen.has(w.widgetId)),
          ];
          setP({
            ...def,
            ...v,
            widgets: mergedWidgets.length > 0 ? mergedWidgets : def.widgets,
            id: def.id,
            label: def.label,
            node: forced ? forceNode : (def.node ?? null),
          });
        }
      })
      .catch(() => {});
    return () => {
      alive = false;
    };
  }, [def.id, forced, forceNode]);

  // Když se forceNode změní (navigace na jiný nod), drž panel.node v sync.
  useEffect(() => {
    if (!forced) return;
    setP((prev) => (prev.node === forceNode ? prev : { ...prev, node: forceNode }));
  }, [forced, forceNode]);

  const persist = (next: Panel) => {
    if (forced) {
      // Layout changes still persist; node changes go to parent.
      if (next.node !== (forceNode ?? null) && onNodeChange) {
        onNodeChange(next.node ?? null);
        // keep layout parts if only node changed — still update widgets if changed
      }
      setP({ ...next, node: forceNode ?? null });
      mcpCall('ui.prefs_set', {
        key: `module-panel:${def.id}`,
        value: { widgets: next.widgets, groups: next.groups ?? [] },
      }).catch(() => {});
      return;
    }
    setP(next);
    mcpCall('ui.prefs_set', {
      key: `module-panel:${def.id}`,
      value: { widgets: next.widgets, groups: next.groups ?? [] },
    }).catch(() => {});
  };

  const resetDefault = () => {
    const base = {
      ...def,
      node: forced ? forceNode : (def.node ?? null),
    };
    setP(base);
    mcpCall('ui.prefs_set', { key: `module-panel:${def.id}`, value: {} }).catch(() => {});
  };

  const panelView = useMemo(() => {
    if (!forced) return p;
    return { ...p, node: forceNode ?? null };
  }, [p, forced, forceNode]);

  return (
    <WidgetPanel
      panel={panelView}
      showTitle={false}
      preferredModule={preferredModule}
      editing={editing}
      onToggleEdit={() => setEditing((e) => !e)}
      onChange={persist}
      onResetDefault={resetDefault}
    />
  );
}
